import { ref } from 'vue'
import type { Ref } from 'vue'
import {
  fetchSwitchPolicy as apiFetchSwitchPolicy,
  upsertSwitchPolicy as apiUpsertSwitchPolicy,
} from '@/api/switchPolicy'
import type { SwitchPolicyData, UpsertSwitchPolicyPayload } from '@/types/switch-policy'

export function useSwitchPolicy(): {
  policy: Ref<SwitchPolicyData | null>
  loading: Ref<boolean>
  saving: Ref<boolean>
  error: Ref<string | null>
  validationErrors: Ref<Record<string, string[]>>
  fetchPolicy: (editionId: number) => Promise<void>
  savePolicy: (editionId: number, payload: UpsertSwitchPolicyPayload) => Promise<boolean>
} {
  const policy = ref<SwitchPolicyData | null>(null)
  const loading = ref<boolean>(false)
  const saving = ref<boolean>(false)
  const error = ref<string | null>(null)
  const validationErrors = ref<Record<string, string[]>>({})

  async function fetchPolicy(editionId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      policy.value = await apiFetchSwitchPolicy(editionId)
    } catch {
      error.value = 'common.error_generic'
    } finally {
      loading.value = false
    }
  }

  async function savePolicy(
    editionId: number,
    payload: UpsertSwitchPolicyPayload,
  ): Promise<boolean> {
    saving.value = true
    error.value = null
    validationErrors.value = {}
    try {
      policy.value = await apiUpsertSwitchPolicy(editionId, payload)
      return true
    } catch (e: unknown) {
      if (isValidationError(e)) {
        validationErrors.value = e.response.data.errors
      } else {
        error.value = 'common.error_generic'
      }
      return false
    } finally {
      saving.value = false
    }
  }

  return { policy, loading, saving, error, validationErrors, fetchPolicy, savePolicy }
}

function isValidationError(
  e: unknown,
): e is { response: { data: { errors: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { data?: unknown } }).response !== null &&
    typeof (e as { response: { data: unknown } }).response.data === 'object'
  )
}
